import { useEffect, useState } from 'react';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';
import { picklistService } from '../services/picklistService';
import type { SerialCommitmentDto } from '../types';
import type { SerialCommitments } from '../utils/serialCommitments';

const NONE: SerialCommitments = new Map<number, SerialCommitmentDto>();

interface Settled {
  key: string;
  bySerial: SerialCommitments;
  error: string | null;
}

/**
 * Which of an item's on-hand serials another open picklist holds (owner, 2026-10-05). One database read, fired once
 * the stock rows are in hand; no NetSuite call.
 *
 * `isLoading` is true from the first render that has serials to ask about, so the dialog keeps its skeleton until the
 * answer lands and never draws a held serial as selectable for a moment. A failed read is reported, not swallowed:
 * the rows then show unlabelled, and the server still refuses a held serial on submit.
 *
 * @param serialIds Sorted and de-duplicated (`serialIdsOf`), so the same serials always make the same key.
 * @param excludePicklistId Add Items only: the picklist being added to, whose own serials are not another's.
 */
export function useSerialCommitments(enabled: boolean, serialIds: readonly number[], excludePicklistId?: number) {
  const key = enabled && serialIds.length > 0 ? `${excludePicklistId ?? 0}:${serialIds.join(',')}` : '';
  const [settled, setSettled] = useState<Settled | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    // Everything the request needs is in the key, so the effect depends on nothing an array identity could move.
    const [exclude, ids] = key.split(':');
    picklistService
      .getSerialCommitments({
        inventoryNumberIds: ids.split(',').map(Number),
        excludePicklistId: Number(exclude) > 0 ? Number(exclude) : null,
      })
      .then((rows) => {
        if (cancelled) return;
        setSettled({ key, bySerial: new Map(rows.map((r) => [r.inventoryNumberId, r] as const)), error: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setSettled({
          key,
          bySerial: NONE,
          error: getApiErrorMessage(error, 'Couldn’t check which serials are on other picklists.'),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [key, attempt]);

  const current = settled !== null && settled.key === key ? settled : null;
  return {
    isLoading: key !== '' && current === null,
    bySerial: current?.bySerial ?? NONE,
    error: current?.error ?? null,
    retry: () => {
      setSettled(null);
      setAttempt((n) => n + 1);
    },
  };
}
